import {
    CameraIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    PhotoIcon
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { ScreenshotAttempt, ScreenshotStatus } from '../../api/client'
import Spinner from '../Spinner'

const statusConfig = {
    [ScreenshotStatus.Success]: {
        icon: CheckCircleIcon,
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-200'
    },
    [ScreenshotStatus.Failed]: {
        icon: XCircleIcon,
        bgColor: 'bg-amber-50',
        textColor: 'text-amber-700',
        borderColor: 'border-amber-200'
    },
    [ScreenshotStatus.Pending]: {
        icon: ClockIcon,
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-700',
        borderColor: 'border-yellow-200'
    }
}

type Props = {
    attempts: ScreenshotAttempt[]
    loading: boolean
    error?: unknown
    onViewImage: (attempt: ScreenshotAttempt) => void
    onViewLogs: (attempt: ScreenshotAttempt) => void
}

function renderDate(value?: string | null) {
    if (!value) return <span className="text-gray-400">-</span>
    try {
        const d = parseISO(value)
        return (
            <div>
                <div className="font-medium">{formatDistanceToNow(d, { addSuffix: true })}</div>
                <div className="text-xs text-gray-500">{d.toLocaleString()}</div>
            </div>
        )
    } catch {
        return <span className="text-gray-500">{value}</span>
    }
}

/**
 * Renders a list of screenshot attempt cards. Used by both ContentAttemptsModal
 * and ScreenshotAttemptsPanel to avoid duplicating the attempt card rendering.
 */
export default function ScreenshotAttemptList({ attempts, loading, error, onViewImage, onViewLogs }: Props) {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Spinner size={6} />
            </div>
        )
    }

    if (error) {
        return <div className="text-red-600 py-4">Error loading attempts: {String(error)}</div>
    }

    if (attempts.length === 0) {
        return (
            <div className="text-gray-500 text-center py-8">
                No screenshot attempts found for this content.
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {attempts.map((attempt) => {
                const config = statusConfig[attempt.status]
                const StatusIcon = config.icon

                return (
                    <div
                        key={attempt.id}
                        className={clsx('border rounded-lg p-4', config.borderColor, config.bgColor)}
                    >
                        <div className="flex items-start gap-4">
                            {/* Screenshot thumbnail */}
                            <div className="flex-shrink-0">
                                {attempt.s3Url ? (
                                    <div className="group relative">
                                        <img
                                            src={attempt.s3Url}
                                            alt={`Screenshot ${attempt.id}`}
                                            className="h-24 w-auto rounded border border-gray-300 hover:border-primary-500 transition-colors cursor-pointer"
                                            onClick={() => onViewImage(attempt)}
                                            onError={(e) => {
                                                const target = e.currentTarget
                                                target.style.display = 'none'
                                                const fallback =
                                                    target.parentElement?.querySelector(
                                                        '.fallback-container'
                                                    )
                                                if (fallback) fallback.classList.remove('hidden')
                                            }}
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded flex items-center justify-center pointer-events-none">
                                            <PhotoIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100" />
                                        </div>
                                        <div className="fallback-container hidden h-24 w-32 bg-gray-200 rounded flex items-center justify-center">
                                            <span className="text-xs text-gray-500 italic">
                                                Not available
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="h-24 w-32 bg-gray-200 rounded flex items-center justify-center">
                                        <CameraIcon className="h-8 w-8 text-gray-400" />
                                    </div>
                                )}
                            </div>

                            {/* Details */}
                            <div className="flex-grow min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                    <StatusIcon
                                        className={clsx('h-5 w-5', config.textColor)}
                                    />
                                    <span className={clsx('font-medium', config.textColor)}>
                                        {attempt.status}
                                    </span>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-sm text-gray-600">
                                        ID: {attempt.id}
                                    </span>
                                </div>

                                <div className="text-sm text-gray-700 mb-2">
                                    {renderDate(attempt.createdAt)}
                                </div>

                                {attempt.errorMessage && (
                                    <div className="mt-2">
                                        <div className="text-xs font-medium text-amber-700 mb-1">
                                            Error:
                                        </div>
                                        <div className="text-sm text-amber-800 bg-amber-100 p-2 rounded font-mono break-words">
                                            {attempt.errorMessage}
                                        </div>
                                    </div>
                                )}

                                {attempt.logs && (
                                    <button
                                        onClick={() => onViewLogs(attempt)}
                                        className="mt-2 text-sm text-primary-600 hover:text-primary-800 hover:underline"
                                    >
                                        View full logs →
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
